import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTabsContext } from './tabs-context.esm.js';
import { TAB_THEME_KEY } from './types.esm.js';
import useForkRef from '../../hooks/use-fork-ref.esm.js';
import useRefresh from '../../hooks/use-refresh.esm.js';
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui.esm.js';
import { isFlatDesign } from '../../themes/const.esm.js';
import { useUniqueId } from '../../utils/unique-id.esm.js';
import Box from '../_primitives/box/box.esm.js';
import ToggleFlipIcon from '../_primitives/icon/toggle-flip-icon.esm.js';
import Adorner from '../_utils/popover/adorner.esm.js';
import Popover from '../_utils/popover/popover.esm.js';
import { ButtonPrivate } from '../button/button.esm.js';
import List from '../list/list.esm.js';
import ListItem from '../list/list-item.esm.js';

var _excluded = ["variant", "placeholder", "children", "includeValues", "paneProps", "buttonList", "popoverProps"],
  _excluded2 = ["ref"];
var responsiveSizes = ['xxs', 'xxs', 'xs', 'sm', 'sm'];
var textProps = {
  fontSize: 'inherit'
};
var MoreTab = function MoreTab(props) {
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'tab' : _props$variant,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? 'More' : _props$placeholder,
    children = props.children,
    includeValues = props.includeValues,
    paneProps = props.paneProps,
    buttonList = props.buttonList,
    popoverProps = props.popoverProps,
    rest = _objectWithoutProperties(props, _excluded);
  var _useTabsContext = useTabsContext(),
    tabVariant = _useTabsContext.variant,
    selectionChangedObserver = _useTabsContext.selectionChangedObserver,
    selection = _useTabsContext.selection;
  var identifier = useUniqueId('csg-tui-more-tab');
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    anchorEl = _useState2[0],
    setAnchorEl = _useState2[1];
  var paneRef = useRef(null);
  var item = includeValues.find(function (i) {
    return i.value === selection;
  });
  var _useState3 = useState(item ? {
      label: item.label,
      icon: item.icon
    } : undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    selectTab = _useState4[0],
    setSelectTab = _useState4[1];
  var theme = useSafeThemeUI(props);
  var isFlatDesign$1 = isFlatDesign(theme);
  useEffect(function () {
    var onChangeHandler = function onChangeHandler(event) {
      var value = event.value,
        label = event.label,
        icon = event.icon;
      var item = includeValues.find(function (i) {
        return i.value === value;
      });
      item ? setSelectTab({
        label: label,
        icon: icon
      }) : setSelectTab(undefined);
      setAnchorEl(null);
    };
    selectionChangedObserver === null || selectionChangedObserver === void 0 ? void 0 : selectionChangedObserver.attach(onChangeHandler);
    return function () {
      return selectionChangedObserver === null || selectionChangedObserver === void 0 ? void 0 : selectionChangedObserver.detach(onChangeHandler);
    };
  }, [selectionChangedObserver, includeValues, setSelectTab]);
  var handleClick = useCallback(function (event) {
    setAnchorEl(event.currentTarget);
  }, []);
  var handleClose = useCallback(function () {
    setAnchorEl(null);
  }, []);
  var open = Boolean(anchorEl);
  var id = open ? identifier : undefined;
  var isTop = function isTop(transformOrigin) {
    return (transformOrigin === null || transformOrigin === void 0 ? void 0 : transformOrigin.vertical) === 'bottom';
  };
  var _ref = popoverProps || {
      children: null
    },
    popoverPaneProps = _ref.paneProps;
  var refreshCallback = useRefresh();
  var _popoverPaneProps$pan = _objectSpread2(_objectSpread2({}, popoverPaneProps), paneProps),
    panePropsRef = _popoverPaneProps$pan.ref,
    restPaneProps = _objectWithoutProperties(_popoverPaneProps$pan, _excluded2);
  var handelPaneRef = useForkRef(paneRef, panePropsRef);
  var handleOnEntered = useCallback(function (paneElement) {
    // @ts-ignore
    handelPaneRef(paneElement);
    refreshCallback();
  }, [handelPaneRef, refreshCallback]);
  var listItems = /*#__PURE__*/React.createElement(List, {
    p: "0",
    variant: "buttonDropdownList",
    onClick: handleClose
  }, React.Children.map(children, function (g, i) {
    return /*#__PURE__*/React.createElement(ListItem, {
      key: i,
      px: "0"
    }, g);
  }));
  var adorner = useCallback(function () {
    return !buttonList ? /*#__PURE__*/React.createElement(Adorner, {
      variant: variant,
      anchorRect: anchorEl ? anchorEl.getBoundingClientRect() : null,
      dropdownRect: paneRef.current ? paneRef.current.getBoundingClientRect() : null,
      isTop: isTop(popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.transformOrigin),
      borderShadowEclipseThickness: 6
    }) : null;
  }, [anchorEl, buttonList, popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.transformOrigin, variant]);
  var iconAfter = useMemo(function () {
    return /*#__PURE__*/React.createElement(ToggleFlipIcon, {
      size: responsiveSizes,
      icon: buttonList ? 'mui-arrowDropDown' : 'chevronDown',
      toggleState: !!anchorEl
    });
  }, [anchorEl, buttonList]);
  var _popoverProps = useMemo(function () {
    return {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right'
      },
      paneProps: _objectSpread2(_objectSpread2({
        variant: tabVariant ? "".concat(tabVariant, ".popover") : 'popover',
        __themeKey: TAB_THEME_KEY,
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
        borderRadius: isFlatDesign$1 ? 'default' : 'none',
        zIndex: 'selectPopover'
      }, paneProps), restPaneProps)
    };
  }, [isFlatDesign$1, paneProps, restPaneProps, tabVariant]);
  return /*#__PURE__*/React.createElement(Box, _extends({
    className: "csg-tui-more-tab-wrapper"
  }, rest), /*#__PURE__*/React.createElement(ButtonPrivate, {
    as: buttonList ? undefined : 'a',
    className: classNames({
      'csg-tui-tab-more': true,
      'is-selected': !!selectTab
    }),
    "aria-describedby": id,
    variant: buttonList ? 'buttonDropdown' : tabVariant ? "".concat(tabVariant, ".tab") : variant,
    __themeKey: buttonList ? undefined : TAB_THEME_KEY,
    toggled: open,
    onClick: handleClick,
    height: buttonList ? '2em' : '100%',
    iconBefore: selectTab === null || selectTab === void 0 ? void 0 : selectTab.icon,
    iconAfter: iconAfter,
    textProps: textProps
  }, selectTab ? selectTab.label : placeholder), /*#__PURE__*/React.createElement(Popover, _extends({
    id: id,
    open: !!anchorEl,
    anchorEl: anchorEl,
    onClose: handleClose,
    onEntered: buttonList ? handleOnEntered : undefined,
    disablePortal: true,
    disableScrollLock: true,
    adornerComponent: isFlatDesign$1 ? undefined : adorner
  }, _popoverProps), buttonList ? listItems : children));
};

export default MoreTab;
//# sourceMappingURL=more-tab.esm.js.map
